import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { rgba } from '@savgroup-front-common/primitive/helpers/cssColor';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';

import { Button } from '../button';
import { Input } from '../Form';

import { SearchInputVariant } from './SearchInput.types';

export const $SearchIconContainer = styled.div`
  position: absolute;
  left: ${spacing('md')};
  height: ${spacing('xxl')};
  display: flex;
  align-items: center;
`;

export const $SearchInputContainer = styled.div<{
  $variant: SearchInputVariant;
  $isActive?: boolean;
  $isHightContrast?: boolean;
}>`
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  height: ${spacing('xxl')};

  svg {
    color: ${({ theme, $isHightContrast }) => {
      return $isHightContrast ? theme.colors.white : theme.colors.mainTextColor;
    }};
  }

  &:hover {
    svg {
      color: ${({ $variant, theme, $isHightContrast }) =>
        $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
          ? $isHightContrast
            ? theme.colors.white
            : theme.colors.mainTextColor
          : null};
    }
  }

  z-index: ${({ $isActive }) => ($isActive ? 12 : null)};
`;

export const $SearchClearButton = styled(Button)<{
  $isHightContrast: boolean;
}>`
  & svg {
    color: ${({ theme, $isHightContrast }) =>
      $isHightContrast ? theme.colors.white : theme.colors.mainTextColor};
  }
`;

export const $SearchActionContainer = styled.div`
  position: absolute;
  right: 0;

  display: flex;
  flex-direction: row;
  align-items: center;
  width: fit-content;
`;

const defaultVariant = css<{ $variant: SearchInputVariant }>`
  background: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.DEFAULT
      ? theme.systemColors.defaultBackground
      : null};
  border-color: ${({ $variant, theme }) =>
    $variant === SearchInputVariant.DEFAULT ? theme.colors.default : null};

  &:focus {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT ? theme.colors.primary : null};
  }
  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT ? theme.colors.primary : null};
    background: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.DEFAULT
        ? theme.systemColors.defaultBackground
        : null};
    box-shadow: ${({ theme }) =>
      `0px 0px 4px ${rgba(theme.colors.primary, 0.4)}`};
  }

  color: ${({ theme }) => theme.colors.mainTextColor};
`;

const globalSearchVariant = css<{ $variant: SearchInputVariant }>`
  background: ${({ theme, $variant }) =>
    $variant === SearchInputVariant.GLOBAL_SEARCH
      ? theme.systemColors.defaultBackground
      : null};
  border-color: ${({ theme, $variant }) =>
    $variant === SearchInputVariant.GLOBAL_SEARCH
      ? theme.colors.default
      : null};

  &:focus {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH
        ? theme.colors.primary
        : null};
  }
  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH
        ? theme.colors.primary
        : null};
    background: ${({ theme, $variant }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH
        ? theme.systemColors.defaultBackground
        : null};
  }
`;

const globalSearchNewUIVariant = css<{
  $variant: SearchInputVariant;
  $isHightContrast?: boolean;
}>`
  background: ${({ $variant, $isHightContrast }) =>
    $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
      ? $isHightContrast
        ? 'transparent'
        : 'rgba(255, 255, 255, 0)'
      : null};

  border-color: ${({ theme, $isHightContrast }) =>
    $isHightContrast ? theme.colors.default : undefined};

  color: ${({ $variant, theme }) => {
    return $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
      ? theme.systemColors.invertedTextColor
      : null;
  }} !important;

  &::placeholder {
    color: ${({ $variant, theme }) => {
      return $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
        ? theme.systemColors.invertedTextColor
        : null;
    }} !important;
  }

  &:focus {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
        ? theme.newUI?.defaultColors?.secondary
        : null};
    box-shadow: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
        ? `0px 0px 6px 0px ${theme.newUI?.defaultColors?.primary}`
        : null};
  }
  &:hover {
    border-color: ${({ $variant, theme }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
        ? theme.newUI?.defaultColors?.secondary
        : null};
    color: ${({ theme, $variant, $isHightContrast }) =>
      $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
        ? $isHightContrast
          ? theme.colors.white
          : theme.colors.mainTextColor
        : null};
    &::placeholder {
      color: ${({ theme, $variant }) =>
        $variant === SearchInputVariant.GLOBAL_SEARCH_NEW_UI
          ? theme.colors.white
          : null};
    }
  }
`;

export const $SearchInput = styled(Input)<{
  $variant: SearchInputVariant;
  $isHightContrast?: boolean;
}>`
  && {
    border: 0;
    height: ${spacing('xxl')};
    &:not([value='']) {
      ~ * ${$SearchClearButton}, ~ ${$SearchClearButton} {
        opacity: 1;
        pointer-events: all;
      }
    }
    &[value=''] {
      ~ * ${$SearchClearButton}, ~ ${$SearchClearButton} {
        pointer-events: none;
      }
    }

    ${defaultVariant};
    ${globalSearchVariant};
    ${globalSearchNewUIVariant};

    border-radius: ${({ theme }) => theme.tw.radius.sm};

    padding: 0 ${spacing(10)} 0 ${spacing(12)};

    flex: 1;

    transition-duration: 0.3s;
    border-width: 1px;
    border-style: solid;

    &:hover,
    &:focus {
      outline: 0;
    }

    @media ${media.maxWidth.xs} {
      padding: 0 ${spacing(10)} 0 ${spacing(12)};
    }
  }
`;

export const $SearchFilter = styled.div`
  && {
    button {
      border: none;
      background: transparent;
      &:hover {
        border: none;
        background: transparent;
      }
    }
  }
`;
